import { useMemo } from 'react';

import { useQuery, queryOptions } from '@tanstack/react-query';

import { STALE_TIMES } from '#lib/queryConstants';

import { fetchProducts, fetchPrices, fetchUserSubscription } from '../../api/billingService';

import { billingKeys } from './keys';

export const productsOptions = () =>
	queryOptions({
		queryKey: billingKeys.products(),
		queryFn: async () => {
			const { data, error } = await fetchProducts();
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.STABLE,
		retry: 2,
	});

export const pricesOptions = () =>
	queryOptions({
		queryKey: billingKeys.prices(),
		queryFn: async () => {
			const { data, error } = await fetchPrices();
			if (error) throw error;
			return data ?? [];
		},
		staleTime: STALE_TIMES.STABLE,
		retry: 2,
	});

export const subscriptionOptions = () =>
	queryOptions({
		queryKey: billingKeys.subscription(),
		queryFn: async () => {
			const { data, error } = await fetchUserSubscription();
			if (error) throw error;
			return data ?? null;
		},
		staleTime: STALE_TIMES.STANDARD,
		retry: 2,
	});

export function useProducts() {
	return useQuery(productsOptions());
}

export function usePrices() {
	return useQuery(pricesOptions());
}

export function useUserSubscription() {
	return useQuery(subscriptionOptions());
}

// Combined plans with prices and upgrade logic
export function usePlansWithPrices() {
	const { data: products = [], isLoading: productsLoading } = useProducts();
	const { data: prices = [], isLoading: pricesLoading } = usePrices();

	const plansWithPrices = useMemo(() => {
		return products
			.map((product) => {
				const productPrices = prices.filter((price) => price.productId === product.id);
				const monthlyPrice = productPrices.find((price) => price.priceInterval === 'month');

				return {
					...product,
					prices: productPrices,
					monthlyPrice: monthlyPrice,
					key: product.stripeProductId,
				};
			})
			.sort((a, b) => (a.monthlyPrice?.unitAmount ?? 0) - (b.monthlyPrice?.unitAmount ?? 0));
	}, [products, prices]);

	return {
		data: plansWithPrices,
		isLoading: productsLoading || pricesLoading,
	};
}

export function useNextUpgradePlan(currentSubscription) {
	const { data: plansWithPrices = [], isLoading } = usePlansWithPrices();

	const nextPlan = useMemo(() => {
		if (!currentSubscription || !plansWithPrices.length) return null;

		const currentPriceAmount = currentSubscription.stripePrices?.unitAmount || 0;

		// Find all plans with higher price
		const higherPlans = plansWithPrices
			.filter((plan) => plan.monthlyPrice && plan.monthlyPrice.unitAmount > currentPriceAmount)
			.sort((a, b) => a.monthlyPrice.unitAmount - b.monthlyPrice.unitAmount);

		return higherPlans[0] || null; // Return cheapest higher plan
	}, [currentSubscription, plansWithPrices]);

	return {
		data: nextPlan,
		isLoading,
	};
}
